import React from "react";
import ReactDOMServer from "react-dom/server";
import { Frame } from "./DialogPlayBuilder.tsx";
import i18next from "../i18n";
import Court from "./Court.tsx";
type CourtType = "halfcourt" | "fullcourt";

type PlayMeta = {
  title: string;
};

const NAMED_COLORS: Record<string, string> = {
  black: "#000000",
  blue: "#0000FF",
  green: "#008000",
  grey: "#808080",
  gray: "#808080",
  maroon: "#800000",
  navy: "#000080",
  orange: "#FFA500",
  purple: "#800080",
  red: "#FF0000",
  white: "#FFFFFF",
  yellow: "#FFFF00",
};

type GradientPalette = {
  highlight: string;
  mid: string;
  shadow: string;
  outline: string;
  limbPrimary: string;
  limbSecondary: string;
};

function normalizeColor(color?: string | null): string | null {
  if (!color) return null;
  const trimmed = color.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (NAMED_COLORS[lower]) return NAMED_COLORS[lower];
  const hexMatch = lower.match(/^#([\da-f]{3}|[\da-f]{6})$/i);
  if (!hexMatch) return null;
  let hex = hexMatch[1];
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }
  return `#${hex.toUpperCase()}`;
}

function adjustColor(color: string, amount: number): string {
  const normalized = normalizeColor(color);
  if (!normalized) return color;
  const hex = normalized.slice(1);
  const num = parseInt(hex, 16);
  const clamp = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)));
  const adjustChannel = (channel: number) =>
    amount >= 0
      ? channel + (255 - channel) * amount
      : channel * (1 + amount);
  let r = (num >> 16) & 255;
  let g = (num >> 8) & 255;
  let b = num & 255;
  r = clamp(adjustChannel(r));
  g = clamp(adjustChannel(g));
  b = clamp(adjustChannel(b));
  const combined = (r << 16) | (g << 8) | b;
  return `#${combined.toString(16).padStart(6, "0").toUpperCase()}`;
}

function getGradientPalette(color: string): GradientPalette {
  const normalized = normalizeColor(color) ?? "#4A4A4A";
  return {
    highlight: adjustColor(normalized, 0.35),
    mid: normalized,
    shadow: adjustColor(normalized, -0.45),
    outline: adjustColor(normalized, -0.6),
    limbPrimary: adjustColor(normalized, -0.25),
    limbSecondary: adjustColor(normalized, -0.4),
  };
}

// SVG CONTENT
export function generateSVGContent(framesData: Frame[], courtType: CourtType, courtColors?: {
  lineColor?: string;
  paintAreaColor?: string | null;
  paintAreaUseWoodPattern?: boolean;
  midRangeColor?: string | null;
  midRangeUseWoodPattern?: boolean;
  outOfBoundsColor?: string | null;
  outOfBoundsUseWoodPattern?: boolean;
}) {
  const courtSVG = ReactDOMServer.renderToStaticMarkup(
    <Court courtType={courtType} {...courtColors} />);
  const [courtWidth, courtHeight] =
    courtType === "fullcourt" ? [1820, 1100] : [1084, 908];

  const allPlayers = framesData.flatMap((f) => f.players);
  const allCones = framesData.flatMap((f) => f.cones || []);

  const uniquePlayerIds = Array.from(new Set(allPlayers.map((p) => p.id)));
  const uniqueConeColors = Array.from(
    new Set(allCones.map((c) => c.color || "#FFA500"))
  );

  const playerMetaById: Record<
    string,
    { baseColor: string; palette: GradientPalette }
  > = {};

  uniquePlayerIds.forEach((id) => {
    const firstAppearance = allPlayers.find((p) => p.id === id);
    const baseColor =
      firstAppearance?.color ||
      (firstAppearance?.isDefender ? "red" : "black") ||
      "#4A4A4A";
    playerMetaById[id] = {
      baseColor,
      palette: getGradientPalette(baseColor),
    };
  });

  const gradientDefs = uniquePlayerIds
    .map((id) => {
      const palette =
        playerMetaById[id]?.palette ?? getGradientPalette("#4A4A4A");
      return `
        <radialGradient id="player-gradient-${id}-head" cx="50%" cy="30%" r="70%">
          <stop offset="0%" stop-color="${palette.highlight}" />
          <stop offset="60%" stop-color="${palette.mid}" />
          <stop offset="100%" stop-color="${palette.shadow}" />
        </radialGradient>
        <linearGradient id="player-gradient-${id}-body" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="${palette.highlight}" />
          <stop offset="55%" stop-color="${palette.mid}" />
          <stop offset="100%" stop-color="${palette.shadow}" />
        </linearGradient>
        <linearGradient id="player-gradient-${id}-limb" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${palette.limbPrimary}" />
          <stop offset="100%" stop-color="${palette.limbSecondary}" />
        </linearGradient>
      `;
    })
    .join("");

  const coneGradientMeta: Record<
    string,
    { gradientId: string; highlightId: string; palette: GradientPalette }
  > = {};

  const coneGradientDefs = uniqueConeColors
    .map((color, idx) => {
      const palette = getGradientPalette(color);
      const gradientId = `cone-gradient-${idx}`;
      const highlightId = `cone-highlight-${idx}`;
      coneGradientMeta[color] = { gradientId, highlightId, palette };
      return `
        <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="${adjustColor(palette.highlight, 0.15)}" />
          <stop offset="55%" stop-color="${palette.mid}" />
          <stop offset="100%" stop-color="${palette.shadow}" />
        </linearGradient>
        <radialGradient id="${highlightId}" cx="50%" cy="25%" r="60%">
          <stop offset="0%" stop-color="${adjustColor(palette.highlight, 0.35)}" stop-opacity="0.85" />
          <stop offset="80%" stop-color="${palette.mid}" stop-opacity="0.05" />
          <stop offset="100%" stop-color="${palette.mid}" stop-opacity="0" />
        </radialGradient>
      `;
    })
    .join("");

  const defsSections: string[] = [];
  if (uniquePlayerIds.length) {
    defsSections.push(`
        <filter id="player-soft-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" flood-color="#000000" flood-opacity="0.35" />
        </filter>
        <radialGradient id="basketball-held-gradient" cx="50%" cy="35%" r="70%">
          <stop offset="0%" stop-color="#FFE6B3" />
          <stop offset="55%" stop-color="#F4831F" />
          <stop offset="100%" stop-color="#B54A12" />
        </radialGradient>
        <radialGradient id="basketball-moving-gradient" cx="45%" cy="35%" r="75%">
          <stop offset="0%" stop-color="#FFE0A6" />
          <stop offset="55%" stop-color="#F07A1C" />
          <stop offset="100%" stop-color="#A6430F" />
        </radialGradient>
        ${gradientDefs}
    `);
  }
  if (coneGradientDefs.trim().length) {
    defsSections.push(coneGradientDefs);
  }

  const defsContent = defsSections.length
    ? `
      <defs>
        ${defsSections.join("\n")}
      </defs>
    `
    : "";

  // Players SVG (uses player colors)
  const playersSVG = uniquePlayerIds
    .map((id) => {
      const player = allPlayers.find((p) => p.id === id);
      if (!player) return "";
      const playerColor =
        player.color || (player.isDefender ? "red" : "black") || "#4A4A4A";
      const palette =
        playerMetaById[id]?.palette ?? getGradientPalette(playerColor);
      const gradientBaseId = `player-gradient-${player.id}`;
      const displayText = player.isCoach
        ? "C"
        : player.isSpecialDefender
          ? String(player.number)
          : player.isDefender
            ? `x${player.number}`
            : String(player.number);
      const charCount = displayText.length;
      let fontSize = 32; // Default for 1 character
      if (charCount === 2) fontSize = 26;
      else if (charCount >= 3) fontSize = 22; // smaller for 3+ characters
      const centerX = player.x * 2;
      const centerY = player.y * 2;

      const heldBallOffsetX = 30;
      const heldBallOffsetY = -8;
      const numberBadgeOffsetY = player.isSpecialDefender ? 0 : 14;
      const numberBadgeRadius = player.isSpecialDefender ? 18 : 15;

      const include3dFigure = !player.isSpecialDefender;

      const groundShadow = `
        <ellipse
          class="player-ground-shadow"
          data-role="ground-shadow"
          data-offset-y="36"
          cx="${centerX}"
          cy="${centerY + 38}"
          rx="34"
          ry="14"
          fill="rgba(0, 0, 0, 0.25)"
        />
      `;

      const silhouette = include3dFigure
        ? `
        <g
          class="player-figure"
          data-role="figure"
          filter="url(#player-soft-shadow)"
          transform="translate(${centerX}, ${centerY})"
        >
          <circle
            data-role="head"
            data-outline-role="primary"
            cx="0"
            cy="-28"
            r="14"
            fill="url(#${gradientBaseId}-head)"
            stroke="${palette.outline}"
            stroke-width="2.5"
          />
          <rect
            data-role="neck"
            x="-4"
            y="-16"
            width="8"
            height="9"
            rx="3"
            fill="${palette.mid}"
            stroke="${palette.outline}"
            stroke-width="2"
          />
          <path
            data-role="torso"
            data-outline-role="primary"
            d="M -22 -4 Q 0 -16 22 -4 L 20 36 Q 0 56 -20 36 Z"
            fill="url(#${gradientBaseId}-body)"
            stroke="${palette.outline}"
            stroke-width="2.5"
            stroke-linejoin="round"
          />
          <path
            data-role="shorts"
            data-outline-role="primary"
            d="M -18 34 L -18 50 L -8 50 L -8 42 Q 0 48 8 42 L 8 50 L 18 50 L 18 34 Q 0 54 -18 34 Z"
            fill="${palette.shadow}"
            stroke="${palette.outline}"
            stroke-width="2.5"
            stroke-linejoin="round"
          />
          <path
            data-role="shorts-divider"
            d="M -16 34 Q 0 42 16 34"
            fill="none"
            stroke="${adjustColor(palette.mid, -0.2)}"
            stroke-width="2.5"
            stroke-linecap="round"
          />
          <rect
            data-role="foot-left"
            data-outline-role="limb"
            data-limb-variant="secondary"
            x="-20"
            y="50"
            width="14"
            height="9"
            rx="4"
            fill="${palette.limbSecondary}"
            stroke="${palette.outline}"
            stroke-width="2"
          />
          <rect
            data-role="foot-right"
            data-outline-role="limb"
            data-limb-variant="secondary"
            x="6"
            y="50"
            width="14"
            height="9"
            rx="4"
            fill="${palette.limbSecondary}"
            stroke="${palette.outline}"
            stroke-width="2"
          />
        </g>
      `
        : "";

      let specialBackground = "";
      let specialForeground = "";
      if (player.isSpecialDefender) {
        const circleRadius = 24;
        const armDistance = 36;
        const armLength = 26;
        const armHeight = 8;
        const armStrokeWidth = 7;
        const armYOffset = 4;
        const rotation = player.armRotation || 0;
        specialBackground = `
          <circle
            data-role="special-ring"
            data-outline-role="primary"
            cx="${centerX}"
            cy="${centerY}"
            r="${circleRadius}"
            fill="url(#${gradientBaseId}-body)"
            stroke="${palette.outline}"
            stroke-width="4"
            filter="url(#player-soft-shadow)"
          />
        `;
        specialForeground = `
          <g class="arms-group" transform="translate(${centerX}, ${centerY}) rotate(${rotation})">
            <g transform="translate(${-armDistance}, ${armYOffset}) rotate(-30)">
              <path
                data-role="special-arm"
                data-arm-layer="shadow"
                d="M -${armLength} ${armYOffset} A ${armLength} ${armHeight} 0 0 1 ${armLength} ${armYOffset}"
                fill="none"
                stroke="${palette.shadow}"
                stroke-width="${armStrokeWidth + 4}"
                stroke-linecap="round"
                stroke-linejoin="round"
                opacity="0.7"
              />
              <path
                data-role="special-arm"
                data-arm-layer="highlight"
                d="M -${armLength} ${armYOffset} A ${armLength} ${armHeight} 0 0 1 ${armLength} ${armYOffset}"
                fill="none"
                stroke="url(#${gradientBaseId}-limb)"
                stroke-width="${armStrokeWidth - 1}"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                data-role="special-arm"
                data-arm-layer="core"
                d="M -${armLength} ${armYOffset} A ${armLength} ${armHeight} 0 0 1 ${armLength} ${armYOffset}"
                fill="none"
                stroke="${palette.highlight}"
                stroke-width="${armStrokeWidth - 3}"
                stroke-linecap="round"
                stroke-linejoin="round"
                opacity="0.8"
              />
            </g>
            <g transform="translate(${armDistance}, ${armYOffset}) rotate(30)">
              <path
                data-role="special-arm"
                data-arm-layer="shadow"
                d="M -${armLength} ${armYOffset} A ${armLength} ${armHeight} 0 0 1 ${armLength} ${armYOffset}"
                fill="none"
                stroke="${palette.shadow}"
                stroke-width="${armStrokeWidth + 4}"
                stroke-linecap="round"
                stroke-linejoin="round"
                opacity="0.7"
              />
              <path
                data-role="special-arm"
                data-arm-layer="highlight"
                d="M -${armLength} ${armYOffset} A ${armLength} ${armHeight} 0 0 1 ${armLength} ${armYOffset}"
                fill="none"
                stroke="url(#${gradientBaseId}-limb)"
                stroke-width="${armStrokeWidth - 1}"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                data-role="special-arm"
                data-arm-layer="core"
                d="M -${armLength} ${armYOffset} A ${armLength} ${armHeight} 0 0 1 ${armLength} ${armYOffset}"
                fill="none"
                stroke="${palette.highlight}"
                stroke-width="${armStrokeWidth - 3}"
                stroke-linecap="round"
                stroke-linejoin="round"
                opacity="0.8"
              />
            </g>
          </g>
        `;
      }

      const heldBall = (include3dFigure || player.isSpecialDefender)
        ? `
        <g
          data-role="held-ball"
          data-offset-x="${heldBallOffsetX}"
          data-offset-y="${heldBallOffsetY}"
          style="${player.hasBall ? "" : "display: none;"}"
          transform="translate(${centerX + heldBallOffsetX}, ${centerY + heldBallOffsetY})"
        >
          <circle r="14" fill="url(#basketball-held-gradient)" stroke="#9E3F10" stroke-width="2.5" />
          <path d="M -14 0 L 14 0" stroke="#9E3F10" stroke-width="1.6" fill="none" stroke-linecap="round" />
          <path d="M 0 -14 A 14 14 0 0 1 0 14" stroke="#9E3F10" stroke-width="1.6" fill="none" stroke-linecap="round" />
        </g>
      `
        : "";

      return `
      <g
        id="player-${player.id}"
        data-player-color="${palette.mid}"
        data-player-base-color="${palette.mid}"
        data-gradient-head="${gradientBaseId}-head"
        data-gradient-body="${gradientBaseId}-body"
        data-gradient-limb="${gradientBaseId}-limb"
        data-is-special-defender="${player.isSpecialDefender ? "true" : "false"}"
        ${player.isSpecialDefender ? `data-arm-rotation="${player.armRotation || 0}"` : ""}
        style="display: none;"
      >
        ${specialBackground}
        ${(include3dFigure || player.isSpecialDefender) ? groundShadow : ""}
        ${include3dFigure ? silhouette : ""}
        ${(include3dFigure || player.isSpecialDefender) ? heldBall : ""}
        ${specialForeground}
        <circle 
          data-role="number-badge"
          data-offset-y="${numberBadgeOffsetY}"
          cx="${centerX}"
          cy="${centerY + numberBadgeOffsetY}"
          r="${numberBadgeRadius}"
          fill="#FFFFFF"
          stroke="#FFFFFF"
          stroke-width="4"
        />
        <text 
          data-offset-y="${numberBadgeOffsetY}"
          x="${centerX}" 
          y="${centerY + numberBadgeOffsetY}" 
          text-anchor="middle" 
          alignment-baseline="middle" 
          font-family="Roboto, sans-serif" 
          font-size="${player.isSpecialDefender ? fontSize * 0.7 : fontSize}" 
          font-weight="bold" 
          fill="${palette.mid}"
          text-rendering="geometricPrecision"
          paint-order="normal"
          stroke="none"
        >
          ${displayText}
        </text>
      </g>
    `;
    })
    .join("");

  // Lines SVG grouped by frame, no IDs
  const linesSVG = framesData
    .map((frame, frameIndex) => {
      const frameLines = frame.lines
        .map((line) => {
          const fromPlayer = allPlayers.find((p) => p.id === line.fromId);
          let lineColor = line.color || (fromPlayer?.color) || (fromPlayer?.isDefender ? "red" : "black");
          if (line.type === "handoff") {
            lineColor = "rgba(255, 255, 255, 0.9)"; // White with slight transparency for handoff main line
          }
          if (line.type === "zigzag" && line.midX !== undefined && line.midY !== undefined) {
            return `
              <path 
                class="line-part1" 
                d="M0 0" 
                stroke="${lineColor}" 
                stroke-width="4" 
                fill="none" 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                style="display: none;"
              />
              <path 
                class="line-part2" 
                d="M0 0" 
                stroke="${lineColor}" 
                stroke-width="4" 
                fill="none" 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                style="display: none;"
              />
            `;
          } else {
            return `
              <path 
                d="M0 0" 
                stroke="${lineColor}" 
                stroke-width="4" 
                fill="none" 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                style="display: none;"
              />
            `;
          }
        })
        .join("");

      // Add handoff symbols separately after the lines to ensure they appear on top
      const handoffSymbols = frame.lines
        .filter(line => line.type === "handoff")
        .map((line) => {
          const fromPlayer = allPlayers.find((p) => p.id === line.fromId);
          const handoffColor = line.handoffColor || line.color || (fromPlayer?.color) || (fromPlayer?.isDefender ? 'red' : 'black');
          return `
            <g class="handoff-symbol" style="display: none;">
              <line class="handoff-horizontal" x1="0" y1="0" x2="0" y2="0" stroke="${handoffColor}" stroke-width="4" />
              <line class="handoff-vertical1" x1="0" y1="0" x2="0" y2="0" stroke="${handoffColor}" stroke-width="4" />
              <line class="handoff-vertical2" x1="0" y1="0" x2="0" y2="0" stroke="${handoffColor}" stroke-width="4" />
            </g>
          `;
        })
        .join("");

      return `<g class="frame-lines" data-frame="${frameIndex}" style="display: none;">${frameLines}${handoffSymbols}</g>`;
    })
    .join("");

  // Cones SVG (unchanged, uses IDs)
  const conesSVG =
    allCones.length > 0
      ? Array.from(new Set(allCones.map((c) => c.id)))
          .map((id) => {
            const cone = allCones.find((c) => c.id === id);
            if (!cone) return "";
            const scale = (24 / 16) * 2;
            const colorKey = cone.color || "#FFA500";
            const meta = coneGradientMeta[colorKey];
            const centerX = cone.x * 2;
            const centerY = cone.y * 2;
            const bodyPath = `
              M${centerX - 6 * scale},${centerY + 7 * scale}
              L${centerX - 3 * scale},${centerY - 6 * scale}
              Q${centerX - 2 * scale},${centerY - 7 * scale},${centerX - 1 * scale},${centerY - 6 * scale}
              L${centerX + 2 * scale},${centerY + 7 * scale}
              L${centerX - 6 * scale},${centerY + 7 * scale}Z 
              M${centerX - 8 * scale},${centerY + 7 * scale}
              Q${centerX - 9 * scale},${centerY + 7 * scale},${centerX - 9 * scale},${centerY + 7.5 * scale}
              L${centerX - 9 * scale},${centerY + 8 * scale}
              L${centerX + 5 * scale},${centerY + 8 * scale}
              L${centerX + 5 * scale},${centerY + 7.5 * scale}
              Q${centerX + 5 * scale},${centerY + 7 * scale},${centerX + 4 * scale},${centerY + 7 * scale}
            `;

            if (!meta) {
              return `
          <g id="cone-${cone.id}" style="display: none;">
            <path
              d="${bodyPath}"
              fill="${colorKey}"
              stroke="${adjustColor(colorKey, -0.35)}"
              stroke-width="${1.2 * scale}"
              stroke-linejoin="round"
            />
          </g>
        `;
            }

            return `
          <g id="cone-${cone.id}" style="display: none;">
            <path
              d="${bodyPath}"
              fill="url(#${meta.gradientId})"
              stroke="${meta.palette.outline}"
              stroke-width="${1.2 * scale}"
              stroke-linejoin="round"
            />
          </g>
        `;
          })
          .join("")
      : "";

  const ressvg = `
    <svg 
      id="animation-svg" 
      viewBox="0 0 ${courtWidth} ${courtHeight}"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      shape-rendering="geometricPrecision"
    >
      <g id="courtSVG">
        ${courtSVG}
      </g>
      ${defsContent}
      <g id="playersSVGlinesSVG">
        ${playersSVG}
        ${linesSVG}
        ${conesSVG}
      </g>
    </svg>
  `;
  return ressvg;
}

// GENERATE ANIMATION SCRIPT
export const generateAnimationScript = (framesData: Frame[], play: PlayMeta, autoDownload: boolean = false) => {
  const DRAW_SPEED_PX_PER_MS = 0.5;
  const ZIGZAG_DRAW_SPEED_PX_PER_MS = 0.8;
  const MARKER_DELAY_SECONDS = 0.5;
  const MOVEMENT_SPEED_PX_PER_MS = 0.6;
  const FPS = 60; // Standard video FPS for better mobile compatibility
  const END_PAUSE_MS = 50;
  const DOWNLOAD_DELAY_MS = 50;

  type GroupMeta = {
    groupTimeMs: number;
    drawTimeMs: number;
    markerTimeMs: number;
    moveTimeMs: number;
    lines: any[];
    combinedPaths?: { [playerId: string]: string };
  };

  const framesMeta = framesData.map((frame) => {
    const linesOrdered = [...frame.lines].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const groups: GroupMeta[] = [];
    let currentGroup: any[] = [];

    for (let i = 0; i < linesOrdered.length; i++) {
      const ln = linesOrdered[i];
      if (i === 0 || !ln.sameMomentAsPrevious) {
        currentGroup = [ln];
        groups.push({
          groupTimeMs: 0,
          drawTimeMs: 0,
          markerTimeMs: 0,
          moveTimeMs: 0,
          lines: currentGroup,
          combinedPaths: {},
        });
      } else {
        currentGroup.push(ln);
      }
    }

    return {
      frame,
      groups,
      frameTimeMs: 0,
    };
  });

  const script = `
    const framesData = ${JSON.stringify(framesData)};
    let framesMeta = ${JSON.stringify(framesMeta)};
    const DRAW_SPEED_PX_PER_MS = ${DRAW_SPEED_PX_PER_MS};
    const ZIGZAG_DRAW_SPEED_PX_PER_MS = ${ZIGZAG_DRAW_SPEED_PX_PER_MS};
    const MARKER_DELAY_SECONDS = ${MARKER_DELAY_SECONDS};
    const MOVEMENT_SPEED_PX_PER_MS = ${MOVEMENT_SPEED_PX_PER_MS};
    const FPS = ${FPS};
    const END_PAUSE_MS = ${END_PAUSE_MS};
    const DOWNLOAD_DELAY_MS = ${DOWNLOAD_DELAY_MS};
    const SCALE_FACTOR = 2;

    let totalDuration = 0;
    let cumulativeFrameDurations = [];
    let isPlaying = false;
    let currentTime = 0;
    let lastTimestamp = null;
    let isRecording = false;

    const playBtn = document.getElementById("play-pause");
    const progressBar = document.getElementById("progress-bar");
    const downloadBtn = document.getElementById("download-video");
    const svgRoot = document.getElementById("animation-svg");
    const courtSVG = document.getElementById("courtSVG");
    const playersSVGlinesSVG = document.getElementById("playersSVGlinesSVG");

    const SVG_NS = "http://www.w3.org/2000/svg";
    const players = {};
    const cones = {};
    const movingElements = {};
    const endElements = {};
    const currentPositions = {};
    const NAMED_COLORS = ${JSON.stringify(NAMED_COLORS)};

    function normalizeColorValue(color) {
      if (!color) return null;
      const trimmed = color.trim();
      if (!trimmed) return null;
      const lower = trimmed.toLowerCase();
      if (NAMED_COLORS[lower]) {
        return NAMED_COLORS[lower];
      }
      const match = lower.match(/^#([\\da-f]{3}|[\\da-f]{6})$/i);
      if (!match) return null;
      let hex = match[1];
      if (hex.length === 3) {
        hex = hex.split("").map((ch) => ch + ch).join("");
      }
      return "#" + hex.toUpperCase();
    }

    function adjustColorValue(color, amount) {
      const normalized = normalizeColorValue(color);
      if (!normalized) return color;
      const num = parseInt(normalized.slice(1), 16);
      const adjustChannel = (channel) =>
        amount >= 0 ? channel + (255 - channel) * amount : channel * (1 + amount);
      const clamp = (value) => Math.min(255, Math.max(0, Math.round(value)));
      let r = (num >> 16) & 255;
      let g = (num >> 8) & 255;
      let b = num & 255;
      r = clamp(adjustChannel(r));
      g = clamp(adjustChannel(g));
      b = clamp(adjustChannel(b));
      return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
    }

    function getGradientPaletteForPlayer(color) {
      const normalized = normalizeColorValue(color) || "#4A4A4A";
      return {
        highlight: adjustColorValue(normalized, 0.35),
        mid: normalized,
        shadow: adjustColorValue(normalized, -0.45),
        outline: adjustColorValue(normalized, -0.6),
        limb: adjustColorValue(normalized, -0.25),
        limbShadow: adjustColorValue(normalized, -0.4),
      };
    }

    function updatePlayerGradients(playerEl, palette) {
      const headId = playerEl.getAttribute("data-gradient-head");
      if (headId) {
        const gradient = document.getElementById(headId);
        if (gradient) {
          const stops = gradient.querySelectorAll("stop");
          if (stops[0]) stops[0].setAttribute("stop-color", palette.highlight);
          if (stops[1]) stops[1].setAttribute("stop-color", palette.mid);
          if (stops[2]) stops[2].setAttribute("stop-color", palette.shadow);
        }
      }
      const bodyId = playerEl.getAttribute("data-gradient-body");
      if (bodyId) {
        const gradient = document.getElementById(bodyId);
        if (gradient) {
          const stops = gradient.querySelectorAll("stop");
          if (stops[0]) stops[0].setAttribute("stop-color", palette.highlight);
          if (stops[1]) stops[1].setAttribute("stop-color", palette.mid);
          if (stops[2]) stops[2].setAttribute("stop-color", palette.shadow);
        }
      }
      const limbId = playerEl.getAttribute("data-gradient-limb");
      if (limbId) {
        const gradient = document.getElementById(limbId);
        if (gradient) {
          const stops = gradient.querySelectorAll("stop");
          if (stops[0]) stops[0].setAttribute("stop-color", palette.limb);
          if (stops[1]) stops[1].setAttribute("stop-color", palette.limbShadow);
        }
      }
    }

    function createBallGroup(radius = 14, gradientId = "basketball-moving-gradient") {
      const group = document.createElementNS(SVG_NS, "g");
      group.setAttribute("data-role", "moving-ball");

      const circle = document.createElementNS(SVG_NS, "circle");
      circle.setAttribute("r", radius.toString());
      circle.setAttribute("fill", gradientId ? "url(#" + gradientId + ")" : "#F07A1C");
      circle.setAttribute("stroke", "#8A380E");
      circle.setAttribute("stroke-width", Math.max(1.6, radius * 0.14).toString());
      group.appendChild(circle);

      const horizontal = document.createElementNS(SVG_NS, "path");
      horizontal.setAttribute("d", "M " + -radius + " 0 L " + radius + " 0");
      horizontal.setAttribute("stroke", "#8A380E");
      horizontal.setAttribute("stroke-width", Math.max(1.2, radius * 0.11).toString());
      horizontal.setAttribute("stroke-linecap", "round");
      horizontal.setAttribute("fill", "none");
      group.appendChild(horizontal);

      const vertical = document.createElementNS(SVG_NS, "path");
      vertical.setAttribute("d", "M 0 " + -radius + " A " + radius + " " + radius + " 0 0 1 0 " + radius);
      vertical.setAttribute("stroke", "#8A380E");
      vertical.setAttribute("stroke-width", Math.max(1.2, radius * 0.11).toString());
      vertical.setAttribute("stroke-linecap", "round");
      vertical.setAttribute("fill", "none");
      group.appendChild(vertical);

      const diagonal = document.createElementNS(SVG_NS, "path");
      const ctrlOffset = radius * 0.55;
      diagonal.setAttribute(
        "d",
        "M " + -ctrlOffset + " " + -radius + " C " + (-radius) + " " + -ctrlOffset + " " + (-radius) + " " + ctrlOffset + " " + -ctrlOffset + " " + radius
      );
      diagonal.setAttribute("stroke", "#8A380E");
      diagonal.setAttribute("stroke-width", Math.max(1.1, radius * 0.1).toString());
      diagonal.setAttribute("stroke-linecap", "round");
      diagonal.setAttribute("fill", "none");
      group.appendChild(diagonal);

      const diagonalMirror = document.createElementNS(SVG_NS, "path");
      diagonalMirror.setAttribute(
        "d",
        "M " + ctrlOffset + " " + -radius + " C " + radius + " " + -ctrlOffset + " " + radius + " " + ctrlOffset + " " + ctrlOffset + " " + radius
      );
      diagonalMirror.setAttribute("stroke", "#8A380E");
      diagonalMirror.setAttribute("stroke-width", Math.max(1.1, radius * 0.1).toString());
      diagonalMirror.setAttribute("stroke-linecap", "round");
      diagonalMirror.setAttribute("fill", "none");
      group.appendChild(diagonalMirror);

      return group;
    }

    // Create overlay elements
    const overlay = document.createElement("div");
    overlay.id = "recording-overlay";
    overlay.style.cssText = "position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: none; z-index: 1000; flex-direction: column; justify-content: center; align-items: center;";
    const overlayText = document.createElement("div");
    overlayText.textContent = "${i18next.t('PlayAnimationHTMLDoc02')}";
    overlayText.style.cssText = "color: white; font-size: 18px; font-family: sans-serif;";
    const timerText = document.createElement("div");
    timerText.style.cssText = "color: white; font-size: 14px; font-family: sans-serif; margin-top: 20px;";
    overlay.appendChild(overlayText);
    overlay.appendChild(timerText);
    document.body.appendChild(overlay);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
    // Use a fixed recording scale so export quality/perf is device-independent.
    const RECORDING_SCALE = 1;
    const [baseWidth, baseHeight] = svgRoot?.getAttribute("viewBox")?.split(" ").slice(2).map(Number) || [1820, 1100];
    canvas.width = baseWidth * RECORDING_SCALE;
    canvas.height = baseHeight * RECORDING_SCALE;
    canvas.style.width = baseWidth + "px";
    canvas.style.height = baseHeight + "px";
    ctx.scale(RECORDING_SCALE, RECORDING_SCALE);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const stream = canvas.captureStream(FPS);
    const videoMimeType = MediaRecorder.isTypeSupported("video/mp4; codecs=avc1") 
      ? "video/mp4; codecs=avc1" 
      : "video/webm";
    const recorder = new MediaRecorder(stream, { 
      mimeType: videoMimeType,
      videoBitsPerSecond: 10000000 // quality
    });

    const chunks = [];

    recorder.ondataavailable = (e) => chunks.push(e.data);
    recorder.onstop = () => {
      const extension = videoMimeType.includes("mp4") ? "mp4" : "webm";
      const blob = new Blob(chunks, { type: videoMimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "${play.title}";
      a.click();
      URL.revokeObjectURL(url);
      if (!videoMimeType.includes("mp4")) {
        alert("Saved as WebM due to browser limitations. For best compatibility, convert to MP4 using VLC or similar software.");
      }
      isRecording = false;
      overlay.style.display = "none";
      enableControls();
    };

    window.onload = () => {
      framesData.forEach(f => {
        f.players.forEach(p => {
          const pe = document.getElementById("player-" + p.id);
          if (pe) players[p.id] = pe;
        });
        (f.cones || []).forEach(c => {
          const ce = document.getElementById("cone-" + c.id);
          if (ce) cones[c.id] = ce;
        });
      });
      measureAndComputeTimings();
      updateAnimation(0);

      if (${autoDownload})
        downloadBtn.click();
    };

    playBtn.onclick = () => {
      if (isRecording) return;
      isPlaying = !isPlaying;
      playBtn.textContent = isPlaying ? "⏸" : "▶";
      if (isPlaying) {
        lastTimestamp = null;
        requestAnimationFrame(animate);
      }
    };

    progressBar.oninput = (e) => {
      if (isRecording) return;
      const value = parseFloat(e.target.value);
      currentTime = (value / 1000) * totalDuration;
      lastTimestamp = null;
      updateAnimation(currentTime);
    };

    downloadBtn.onclick = () => {
      if (!MediaRecorder.isTypeSupported(videoMimeType)) {
        alert("Video recording is not supported in this browser.");
        return;
      }
      if (isRecording) return;

      isRecording = true;
      disableControls();
      overlay.style.display = "flex";
      chunks.length = 0;
      currentTime = 0;
      updateAnimation(0);
      renderToCanvas();
      
      setTimeout(() => {
        recorder.start();
        isPlaying = true;
        playBtn.textContent = "⏸";
        lastTimestamp = null;
        requestAnimationFrame(recordAnimation);
      }, DOWNLOAD_DELAY_MS);
    };

    //Illustrate download time while recording
    function updateTimer() {
      if (!isRecording) return;
      const timeLeft = Math.max(0, totalDuration - currentTime);
      const secondsLeft = (timeLeft / 1000).toFixed(1);
      timerText.textContent = \`${i18next.t('PlayAnimationHTMLDoc01')} \${secondsLeft}s\`;
    }

    function disableControls() {
      playBtn.disabled = true;
      downloadBtn.disabled = true;
    }

    function enableControls() {
      playBtn.disabled = false;
      downloadBtn.disabled = false;
    }

    async function animate(timestamp) {
      if (!isPlaying) return;
      if (!lastTimestamp) lastTimestamp = timestamp;
      const delta = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
      currentTime += delta;

      if (currentTime > totalDuration) {
        currentTime = totalDuration;
        isPlaying = false;
        playBtn.textContent = "▶";
      }
      if (currentTime <= totalDuration - END_PAUSE_MS) {
        updateAnimation(currentTime);
      }
      requestAnimationFrame(animate);
    }

    async function recordAnimation(timestamp) {
      if (!isPlaying) {
        recorder.stop();
        return;
      }
      if (!lastTimestamp) lastTimestamp = timestamp;
      const delta = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
      currentTime += delta;

      updateTimer();

      if (currentTime > totalDuration) {
        currentTime = totalDuration;
        isPlaying = false;
        playBtn.textContent = "▶";
        recorder.stop();
      } else {
        if (currentTime <= totalDuration - END_PAUSE_MS) {
          updateAnimation(currentTime);
        }
        renderToCanvas();
        requestAnimationFrame(recordAnimation);
      }
    }

    function renderToCanvas() {
      const svgData = new XMLSerializer().serializeToString(svgRoot);
      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, baseWidth, baseHeight);
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    }

    function updateAnimation(time) {
      time = Math.min(time, totalDuration);
      if (totalDuration <= 0) return;

      progressBar.value = (time / totalDuration) * 1000;

      let frameIndex = cumulativeFrameDurations.findIndex(dur => time < dur);
      if (frameIndex === -1) frameIndex = framesMeta.length - 1;

      resetAll();

      const frameStart = frameIndex === 0 ? 0 : cumulativeFrameDurations[frameIndex - 1];
      const timeInFrame = time - frameStart;

      const fm = framesMeta[frameIndex];
      if (!fm) return;

      const { frame, groups } = fm;

      const frameGroups = document.querySelectorAll(".frame-lines");
      frameGroups.forEach((group, idx) => {
        group.style.display = idx === frameIndex ? "" : "none";
      });
      const currentFrameGroup = frameGroups[frameIndex];
      const frameLines = currentFrameGroup.querySelectorAll("path");

      frame.players.forEach(p => {
        currentPositions[p.id] = { x: p.x * SCALE_FACTOR, y: p.y * SCALE_FACTOR };
      });

      frame.players.forEach(p => {
        const pe = players[p.id];
        if (pe) {
          pe.style.display = "";
          // Store armRotation for special defenders
          if (p.isSpecialDefender && p.armRotation !== undefined) {
            pe.setAttribute("data-arm-rotation", p.armRotation.toString());
          }
          // Update player color based on current frame
          updatePlayerColor(pe, p);
          setPlayerPos(pe, currentPositions[p.id].x, currentPositions[p.id].y);
          toggleBall(pe, p.hasBall);
        }
      });

      frame.cones?.forEach(c => {
        const ce = cones[c.id];
        if (ce) ce.style.display = "";
      });

      let currentBallHolders = new Set(frame.players.filter(p => p.hasBall).map(p => p.id));
      let groupStartMs = 0;
      let lineIndexOffset = 0;

      for (let gIdx = 0; gIdx < groups.length; gIdx++) {
        const g = groups[gIdx];
        const groupEndMs = groupStartMs + g.groupTimeMs;
        let localTime = timeInFrame - groupStartMs;

        if (localTime < 0) break;
        if (localTime > g.groupTimeMs) localTime = g.groupTimeMs;

        const { drawTimeMs, markerTimeMs, moveTimeMs, combinedPaths = {} } = g;
        const phase2Start = drawTimeMs;
        const phase2End = drawTimeMs + markerTimeMs;

        let groupLineIndex = lineIndexOffset; // Start index for this group

        g.lines.forEach((ln, lnIdx) => {
          const isDoubleZigzag = ln.type === "zigzag" && ln.midX !== undefined && ln.midY !== undefined;
          const lineIndex = groupLineIndex; // Index for this line's first path

          const lineEl = isDoubleZigzag ? frameLines[lineIndex] : frameLines[lineIndex];
          if (!lineEl) return;

          setPathD(lineEl, ln, frame.players, frameLines, lineIndex);

          if (localTime <= phase2Start) {
            const partialMs = localTime;
            drawLineSegment(lineEl, ln, partialMs, drawTimeMs, frameLines, lineIndex);
          } else if (localTime <= phase2End) {
            fullyDrawLine(lineEl, ln, frameLines, lineIndex);
          } else {
            if (isDoubleZigzag) {
              const lineElPart1 = frameLines[lineIndex];
              const lineElPart2 = frameLines[lineIndex + 1];
              if (lineElPart1) lineElPart1.style.display = "none";
              if (lineElPart2) lineElPart2.style.display = "none";
            } else {
              lineEl.style.display = "none";
            }
            removeEndElement(ln.id);

            const fromP = frame.players.find(p => p.id === ln.fromId);
            const toP = ln.toId ? frame.players.find(p => p.id === ln.toId) : null;

            const timeIntoPhase3 = localTime - phase2End;
            const moveFrac = moveTimeMs > 0 ? (timeIntoPhase3 / moveTimeMs) : 1;

            if ((ln.type === "dashed" || ln.type === "handoff") && fromP && currentBallHolders.has(fromP.id)) {
              currentBallHolders.delete(fromP.id);
              removePlayerBall(fromP.id);
              animateBallAlongPath(lineEl, ln, moveFrac, fromP, toP);
              if (moveFrac >= 1 && toP) {
                currentBallHolders.add(toP.id);
                toggleBall(players[toP.id], true);
                removeMovingElement(ln.id);
              }
            } else if (ln.type === "zigzag" && fromP && currentBallHolders.has(fromP.id)) {
              animatePlayerWithBall(lineEl, ln, moveFrac, fromP, combinedPaths);
            } else if (ln.type === "shot" && fromP && currentBallHolders.has(fromP.id)) {
              currentBallHolders.delete(fromP.id);
              removePlayerBall(fromP.id);
              animateShotAlongPath(lineEl, ln, moveFrac, fromP);
              if (moveFrac >= 1) {
                removeMovingElement(ln.id);
              }
            } else if (fromP && (ln.type === "straight" || ln.type === "bar")) {
              animateAlongPath(lineEl, ln, moveFrac, fromP, combinedPaths);
            }
          }

          // Increment groupLineIndex based on line type
          groupLineIndex += isDoubleZigzag ? 2 : 1;
        });

        lineIndexOffset = groupLineIndex; // Update offset for next group
        groupStartMs = groupEndMs;
      }

      frame.players.forEach(p => {
        const pe = players[p.id];
        if (pe) {
          // Update armRotation for special defenders
          if (p.isSpecialDefender && p.armRotation !== undefined) {
            pe.setAttribute("data-arm-rotation", p.armRotation.toString());
          }
          // Update player color based on current frame
          updatePlayerColor(pe, p);
          setPlayerPos(pe, currentPositions[p.id].x, currentPositions[p.id].y);
          toggleBall(pe, currentBallHolders.has(p.id));
        }
      });
    }

    function measureAndComputeTimings() {
      totalDuration = 0;
      cumulativeFrameDurations = [];

      for (let fIdx = 0; fIdx < framesMeta.length; fIdx++) {
        const fm = framesMeta[fIdx];
        let frameTime = 0;
        const frameLines = document.querySelector(\`.frame-lines[data-frame="\${fIdx}"]\`).querySelectorAll("path");
        let lineIndexOffset = 0;

        fm.groups.forEach(g => {
          let maxDrawTime = 0;
          let maxMoveTime = 0;
          const playerLines = {};
          const combinedPaths = {};

          g.lines.forEach(ln => {
            if (!playerLines[ln.fromId]) playerLines[ln.fromId] = [];
            playerLines[ln.fromId].push(ln);
          });

          Object.keys(playerLines).forEach(playerId => {
            const lines = playerLines[playerId];
            if (lines.length > 1) {
              let combinedD = "M " + (lines[0].fromX * SCALE_FACTOR) + " " + (lines[0].fromY * SCALE_FACTOR);
              lines.forEach(ln => {
                if (ln.midX !== undefined && ln.midY !== undefined && ln.controlX2 !== undefined && ln.controlY2 !== undefined) {
                  combinedD += " Q " + (ln.controlX * SCALE_FACTOR) + " " + (ln.controlY * SCALE_FACTOR) + " " + (ln.midX * SCALE_FACTOR) + " " + (ln.midY * SCALE_FACTOR) + " Q " + (ln.controlX2 * SCALE_FACTOR) + " " + (ln.controlY2 * SCALE_FACTOR) + " " + (ln.toX * SCALE_FACTOR) + " " + (ln.toY * SCALE_FACTOR);
                } else {
                  combinedD += " Q " + (ln.controlX * SCALE_FACTOR) + " " + (ln.controlY * SCALE_FACTOR) + " " + (ln.toX * SCALE_FACTOR) + " " + (ln.toY * SCALE_FACTOR);
                }
              });
              combinedPaths[playerId] = combinedD;
            }
          });

          g.combinedPaths = combinedPaths;

          let groupLineIndex = lineIndexOffset;

          g.lines.forEach((ln) => {
            const isDoubleZigzag = ln.type === "zigzag" && ln.midX !== undefined && ln.midY !== undefined;
            const lineIndex = groupLineIndex;
            let drawLength = 0;
            let moveLength = 0;
            let drawSpeed = DRAW_SPEED_PX_PER_MS;

            if (isDoubleZigzag) {
              drawSpeed = ZIGZAG_DRAW_SPEED_PX_PER_MS;
              const lineElPart1 = frameLines[lineIndex];
              const lineElPart2 = frameLines[lineIndex + 1];
              if (lineElPart1 && lineElPart2) {
                setPathD(lineElPart1, ln, fm.frame.players, frameLines, lineIndex);
                setPathD(lineElPart2, ln, fm.frame.players, frameLines, lineIndex);
                drawLength = lineElPart1.getTotalLength() + lineElPart2.getTotalLength();
              }
              const fromX = ln.fromX * SCALE_FACTOR;
              const fromY = ln.fromY * SCALE_FACTOR;
              const ctrlX = ln.controlX * SCALE_FACTOR;
              const ctrlY = ln.controlY * SCALE_FACTOR;
              const midX = ln.midX * SCALE_FACTOR;
              const midY = ln.midY * SCALE_FACTOR;
              const ctrlX2 = ln.controlX2 * SCALE_FACTOR;
              const ctrlY2 = ln.controlY2 * SCALE_FACTOR;
              const toX = ln.toX * SCALE_FACTOR;
              const toY = ln.toY * SCALE_FACTOR;
              const smoothD = \`M \${fromX} \${fromY} Q \${ctrlX} \${ctrlY} \${midX} \${midY} Q \${ctrlX2} \${ctrlY2} \${toX} \${toY}\`;
              const tempPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
              tempPath.setAttribute("d", smoothD);
              svgRoot.appendChild(tempPath);
              moveLength = tempPath.getTotalLength();
              svgRoot.removeChild(tempPath);
            } else {
              const lineEl = frameLines[lineIndex];
              if (lineEl) {
                setPathD(lineEl, ln, fm.frame.players, frameLines, lineIndex);
                drawLength = lineEl.getTotalLength();
                moveLength = drawLength;
              }
            }

            const individualDrawTime = drawLength / drawSpeed;
            const individualMoveTime = moveLength / MOVEMENT_SPEED_PX_PER_MS;

            if (individualDrawTime > maxDrawTime) maxDrawTime = individualDrawTime;
            if (individualMoveTime > maxMoveTime) maxMoveTime = individualMoveTime;

            groupLineIndex += isDoubleZigzag ? 2 : 1;
          });

          const drawTimeMs = maxDrawTime;
          const markerTimeMs = MARKER_DELAY_SECONDS * 1000;
          const moveTimeMs = maxMoveTime;
          const groupTimeMs = drawTimeMs + markerTimeMs + moveTimeMs;

          g.drawTimeMs = drawTimeMs;
          g.markerTimeMs = markerTimeMs;
          g.moveTimeMs = moveTimeMs;
          g.groupTimeMs = groupTimeMs;

          frameTime += groupTimeMs;
          lineIndexOffset = groupLineIndex;
        });

        fm.frameTimeMs = frameTime;
        totalDuration += frameTime;
        cumulativeFrameDurations.push(totalDuration);
      }

      totalDuration += END_PAUSE_MS;
      cumulativeFrameDurations[cumulativeFrameDurations.length - 1] = totalDuration;
    }

    function getLineColor(ln) {
      if (ln.type === "handoff") {
        return "rgba(255, 255, 255, 0.9)"; // White with slight transparency for handoff main line
      }
      if (ln.color) {
        return ln.color;
      }
      const fromPlayerEl = players[ln.fromId];
      const playerColor = fromPlayerEl ? fromPlayerEl.getAttribute("data-player-color") : null;
      return playerColor || "black";
    }

    function drawLineSegment(lineEl, ln, partialMs, totalDrawMs, frameLines, baseIndex) {
      lineEl.setAttribute("stroke", getLineColor(ln));

      if (ln.type === "zigzag" && ln.midX !== undefined && ln.midY !== undefined) {
        const lineElPart1 = frameLines[baseIndex];
        const lineElPart2 = frameLines[baseIndex + 1];
        if (!lineElPart1 || !lineElPart2) return;

        lineElPart1.setAttribute("stroke", getLineColor(ln));
        lineElPart2.setAttribute("stroke", getLineColor(ln));

        const length1 = lineElPart1.getTotalLength();
        const length2 = lineElPart2.getTotalLength();
        const totalLength = length1 + length2;

        const minDrawTimeMs = totalLength / ZIGZAG_DRAW_SPEED_PX_PER_MS;
        const effectiveDrawTimeMs = Math.max(totalDrawMs, minDrawTimeMs);

        const drawTimePart1 = (length1 / totalLength) * effectiveDrawTimeMs;
        const drawTimePart2 = (length2 / totalLength) * effectiveDrawTimeMs;

        if (partialMs <= drawTimePart1) {
          const frac1 = partialMs / drawTimePart1;
          const pxDrawn1 = length1 * Math.min(frac1, 1);
          lineElPart1.style.display = "";
          lineElPart1.setAttribute("stroke-dasharray", length1);
          lineElPart1.setAttribute("stroke-dashoffset", length1 - pxDrawn1);
          lineElPart2.style.display = "none";
          removeEndElement(ln.id);
        } else {
          lineElPart1.style.display = "";
          lineElPart1.setAttribute("stroke-dasharray", length1);
          lineElPart1.setAttribute("stroke-dashoffset", 0);

          const timeIntoPart2 = partialMs - drawTimePart1;
          const frac2 = timeIntoPart2 / drawTimePart2;
          const pxDrawn2 = length2 * Math.min(frac2, 1);
          lineElPart2.style.display = "";
          lineElPart2.setAttribute("stroke-dasharray", length2);
          lineElPart2.setAttribute("stroke-dashoffset", length2 - pxDrawn2);

          if (frac2 >= 1) {
            fullyDrawLine(lineElPart2, ln, frameLines, baseIndex);
          } else {
            removeEndElement(ln.id);
          }
        }
      } else {
        const length = lineEl.getTotalLength();
        const frac = totalDrawMs > 0 ? partialMs / totalDrawMs : 1;
        const pxDrawn = length * frac;
        if (pxDrawn <= 0) {
          lineEl.style.display = "none";
          if (ln.type === "handoff") {
            hideHandoffSymbol(ln, frameLines, baseIndex);
          }
        } else if (pxDrawn >= length) {
          fullyDrawLine(lineEl, ln, frameLines, baseIndex);
        } else {
          lineEl.style.display = "";
          lineEl.setAttribute("stroke-dasharray", length);
          lineEl.setAttribute("stroke-dashoffset", length - pxDrawn);
          removeEndElement(ln.id);
          
          // Show handoff symbol when line is fully drawn
          if (ln.type === "handoff" && frac >= 0.5) {
            showHandoffSymbol(ln, frameLines, baseIndex);
          } else if (ln.type === "handoff") {
            hideHandoffSymbol(ln, frameLines, baseIndex);
          }
        }
      }
    }

    function fullyDrawLine(lineEl, ln, frameLines, baseIndex) {
      if (ln.type === "zigzag" && ln.midX !== undefined && ln.midY !== undefined) {
        const lineElPart1 = frameLines[baseIndex];
        const lineElPart2 = frameLines[baseIndex + 1];
        if (lineElPart1) {
          lineElPart1.style.display = "";
          lineElPart1.setAttribute("stroke-dasharray", lineElPart1.getTotalLength());
          lineElPart1.setAttribute("stroke-dashoffset", 0);
          lineElPart1.setAttribute("stroke", getLineColor(ln));
        }
        if (lineElPart2) {
          lineElPart2.style.display = "";
          lineElPart2.setAttribute("stroke-dasharray", lineElPart2.getTotalLength());
          lineElPart2.setAttribute("stroke-dashoffset", 0);
          lineElPart2.setAttribute("stroke", getLineColor(ln));
          ensureEndElement(ln, lineElPart2);
        }
      } else {
        lineEl.style.display = "";
        const length = lineEl.getTotalLength();
        lineEl.setAttribute("stroke-dasharray", length);
        lineEl.setAttribute("stroke-dashoffset", 0);
        if (ln.type === "dashed" || ln.type === "shot" || ln.type === "handoff") {
          lineEl.setAttribute("stroke-dasharray", "10,10");
        }
        lineEl.setAttribute("stroke", getLineColor(ln));
        ensureEndElement(ln, lineEl);
        
        // Show handoff symbol if it's a handoff line
        if (ln.type === "handoff") {
          showHandoffSymbol(ln, frameLines, baseIndex);
        }
      }
    }

    function animateBallAlongPath(pathEl, ln, fraction, fromP, toP) {
      const offsetD = pathEl.getAttribute("data-offset-d");
      const fullD = pathEl.getAttribute("data-full-d");
      if (!fullD) return;

      pathEl.setAttribute("d", fullD);
      const len = pathEl.getTotalLength();
      const point = pathEl.getPointAtLength(len * fraction);
      pathEl.setAttribute("d", offsetD);

      if (!movingElements[ln.id]) {
        const ballGroup = createBallGroup(13);
        svgRoot.appendChild(ballGroup);
        movingElements[ln.id] = ballGroup;
      }
      movingElements[ln.id].setAttribute("transform", "translate(" + point.x + ", " + point.y + ")");

      if (fraction >= 1 && toP) {
        removeMovingElement(ln.id);
        toggleBall(players[toP.id], true);
      }
    }

    function animatePlayerWithBall(pathEl, ln, fraction, fromP, combinedPaths) {
      const offsetD = pathEl.getAttribute("data-offset-d");
      const fullD = pathEl.getAttribute("data-full-d");
      if (!fullD) return;

      let smoothPathEl;
      if (ln.type === "zigzag" && ln.midX !== undefined && ln.midY !== undefined) {
        const fromX = ln.fromX * SCALE_FACTOR;
        const fromY = ln.fromY * SCALE_FACTOR;
        const ctrlX = ln.controlX * SCALE_FACTOR;
        const ctrlY = ln.controlY * SCALE_FACTOR;
        const midX = ln.midX * SCALE_FACTOR;
        const midY = ln.midY * SCALE_FACTOR;
        const ctrlX2 = ln.controlX2 * SCALE_FACTOR;
        const ctrlY2 = ln.controlY2 * SCALE_FACTOR;
        const toX = ln.toX * SCALE_FACTOR;
        const toY = ln.toY * SCALE_FACTOR;
        smoothPathEl = document.createElementNS("http://www.w3.org/2000/svg", "path");
        smoothPathEl.setAttribute("d", \`M \${fromX} \${fromY} Q \${ctrlX} \${ctrlY} \${midX} \${midY} Q \${ctrlX2} \${ctrlY2} \${toX} \${toY}\`);
      } else {
        smoothPathEl = document.createElementNS("http://www.w3.org/2000/svg", "path");
        smoothPathEl.setAttribute("d", fullD);
      }

      const playerId = fromP.id;
      if (combinedPaths && playerId && combinedPaths[playerId]) {
        smoothPathEl.setAttribute("d", combinedPaths[playerId]);
      }

      svgRoot.appendChild(smoothPathEl);
      const len = smoothPathEl.getTotalLength();
      const point = smoothPathEl.getPointAtLength(len * fraction);
      svgRoot.removeChild(smoothPathEl);

      const playerEl = players[fromP.id];
      if (playerEl) {
        setPlayerPos(playerEl, point.x, point.y);
        toggleBall(playerEl, true);
        currentPositions[fromP.id] = { x: point.x, y: point.y };
      }
    }

    function animateAlongPath(pathEl, ln, fraction, fromP, combinedPaths) {
      const offsetD = pathEl.getAttribute("data-offset-d");
      const fullD = pathEl.getAttribute("data-full-d");
      if (!fullD) return;

      let pathToUse = fullD;
      const playerId = fromP.id;
      if (combinedPaths && playerId && combinedPaths[playerId]) {
        pathToUse = combinedPaths[playerId];
      }

      pathEl.setAttribute("d", pathToUse);
      const len = pathEl.getTotalLength();
      const point = pathEl.getPointAtLength(len * fraction);
      pathEl.setAttribute("d", offsetD);

      const playerEl = players[fromP.id];
      if (playerEl) {
        setPlayerPos(playerEl, point.x, point.y);
        currentPositions[fromP.id] = { x: point.x, y: point.y };
      }
    }

    function animateShotAlongPath(pathEl, ln, fraction, fromP) {
      const offsetD = pathEl.getAttribute("data-offset-d");
      const fullD = pathEl.getAttribute("data-full-d");
      if (!fullD) return;

      pathEl.setAttribute("d", fullD);
      const len = pathEl.getTotalLength();
      const point = pathEl.getPointAtLength(len * fraction);
      pathEl.setAttribute("d", offsetD);

      if (!movingElements[ln.id]) {
        const shotBall = createBallGroup(12);
        svgRoot.appendChild(shotBall);
        movingElements[ln.id] = shotBall;
      }
      movingElements[ln.id].setAttribute("transform", "translate(" + point.x + ", " + point.y + ")");

      if (fraction >= 1) {
        removeMovingElement(ln.id);
      }
    }

    function setPathD(lineEl, ln, framePlayers, frameLines, baseIndex) {
      const pMap = {};
      framePlayers.forEach(p => { pMap[p.id] = p; });

      const fromP = pMap[ln.fromId];
      if (!fromP) return;

      const fromPos = currentPositions[ln.fromId] || { x: ln.fromX * SCALE_FACTOR, y: ln.fromY * SCALE_FACTOR };
      const fromFull = { x: fromPos.x, y: fromPos.y };
      const toFull = { x: ln.toX * SCALE_FACTOR, y: ln.toY * SCALE_FACTOR };
      const controlPoint = { x: ln.controlX * SCALE_FACTOR, y: ln.controlY * SCALE_FACTOR };
      let OFFSET_RADIUS = 36;

      let fromOffset = OFFSET_RADIUS;
      let toOffset = OFFSET_RADIUS;

      if (ln.type === "bar" || ln.type === "straight") {
        fromOffset = 26;
      }

      if (ln.type === "dashed" || ln.type === "handoff") {
        toOffset = 60;
      }

      // Adjust for zigzag: -2 for zigzag, +9 for non-zigzag (scaled for animation)
      if (ln.type === "zigzag") {
        fromOffset = fromOffset - 2;
        toOffset = toOffset - 2;
      } else {
        fromOffset = fromOffset + 9;
        toOffset = toOffset + 9;
      }

      let dValueFull = "";
      let dValueOffset = "";

      if (ln.midX !== undefined && ln.midY !== undefined && ln.controlX2 !== undefined && ln.controlY2 !== undefined) {
        const midFull = { x: ln.midX * SCALE_FACTOR, y: ln.midY * SCALE_FACTOR };
        const controlPoint1 = { x: ln.controlX * SCALE_FACTOR, y: ln.controlY * SCALE_FACTOR };
        const controlPoint2 = { x: ln.controlX2 * SCALE_FACTOR, y: ln.controlY2 * SCALE_FACTOR };

        const startOffset = getOffsetPoint(fromFull.x, fromFull.y, controlPoint1.x, controlPoint1.y, fromOffset);
        const endOffset = getOffsetPoint(toFull.x, toFull.y, controlPoint2.x, controlPoint2.y, toOffset);

        if (ln.type === "zigzag") {
          const lineElPart1 = frameLines[baseIndex];
          const lineElPart2 = frameLines[baseIndex + 1];

          const length1 = approximateBezierLength(startOffset, controlPoint1, midFull);
          const length2 = approximateBezierLength(midFull, controlPoint2, endOffset);
          const totalLength = length1 + length2;
          const totalOsc = Math.max(2, Math.round((totalLength / 100) * 8));
          const freq = (totalOsc * Math.PI) / totalLength;

          const zigzag1 = createZigzagPath(startOffset, controlPoint1, midFull, 7, freq, 0);
          const zigzag2 = createZigzagPath(midFull, controlPoint2, endOffset, 7, freq, length1, true);

          dValueFull = "M " + fromFull.x + " " + fromFull.y + " Q " + controlPoint1.x + " " + controlPoint1.y + " " + midFull.x + " " + midFull.y + " Q " + controlPoint2.x + " " + controlPoint2.y + " " + toFull.x + " " + toFull.y;
          lineElPart1.setAttribute("d", zigzag1.d);
          lineElPart2.setAttribute("d", zigzag2.d);
          lineElPart1.setAttribute("data-full-d", "M " + fromFull.x + " " + fromFull.y + " Q " + controlPoint1.x + " " + controlPoint1.y + " " + midFull.x + " " + midFull.y);
          lineElPart2.setAttribute("data-full-d", "M " + midFull.x + " " + midFull.y + " Q " + controlPoint2.x + " " + controlPoint2.y + " " + toFull.x + " " + toFull.y);
          lineElPart1.setAttribute("data-offset-d", zigzag1.d);
          lineElPart2.setAttribute("data-offset-d", zigzag2.d);
          lineElPart1.setAttribute("data-zigzag-length", zigzag1.totalLength.toString());
          lineElPart2.setAttribute("data-zigzag-length", zigzag2.totalLength.toString());
        } else {
          dValueFull = "M " + fromFull.x + " " + fromFull.y + " Q " + controlPoint1.x + " " + controlPoint1.y + " " + midFull.x + " " + midFull.y + " Q " + controlPoint2.x + " " + controlPoint2.y + " " + toFull.x + " " + toFull.y;
          dValueOffset = "M " + startOffset.x + " " + startOffset.y + " Q " + controlPoint1.x + " " + controlPoint1.y + " " + midFull.x + " " + midFull.y + " Q " + controlPoint2.x + " " + controlPoint2.y + " " + endOffset.x + " " + endOffset.y;
          lineEl.setAttribute("d", dValueOffset);
          lineEl.setAttribute("data-full-d", dValueFull);
          lineEl.setAttribute("data-offset-d", dValueOffset);
        }
      } else {
        const startOffset = getOffsetPoint(fromFull.x, fromFull.y, controlPoint.x, controlPoint.y, fromOffset);
        const endOffset = getOffsetPoint(toFull.x, toFull.y, controlPoint.x, controlPoint.y, toOffset);

        if (ln.type === "zigzag") {
          const length = approximateBezierLength(startOffset, controlPoint, endOffset);
          const totalOsc = Math.max(2, Math.round((length / 100) * 8));
          const freq = (totalOsc * Math.PI) / length;
          const zigzagPath = createZigzagPath(startOffset, controlPoint, endOffset, 7, freq, 0, true);
          dValueOffset = zigzagPath.d;
          dValueFull = "M " + fromFull.x + " " + fromFull.y + " Q " + Math.round(controlPoint.x) + " " + Math.round(controlPoint.y) + " " + Math.round(toFull.x) + " " + Math.round(toFull.y);
          lineEl.setAttribute("d", dValueOffset);
          lineEl.setAttribute("data-full-d", dValueFull);
          lineEl.setAttribute("data-offset-d", dValueOffset);
        } else {
          dValueFull = "M " + Math.round(fromFull.x) + " " + Math.round(fromFull.y) + " Q " + Math.round(controlPoint.x) + " " + Math.round(controlPoint.y) + " " + Math.round(toFull.x) + " " + Math.round(toFull.y);
          dValueOffset = "M " + Math.round(startOffset.x) + " " + Math.round(startOffset.y) + " Q " + Math.round(controlPoint.x) + " " + Math.round(controlPoint.y) + " " + Math.round(endOffset.x) + " " + Math.round(endOffset.y);
          lineEl.setAttribute("d", dValueOffset);
          lineEl.setAttribute("data-full-d", dValueFull);
          lineEl.setAttribute("data-offset-d", dValueOffset);
        }
      }
    }

    function approximateBezierLength(from, ctrl, to) {
      let length = 0, steps = 20, prev = from;
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const x = (1 - t)**2 * from.x + 2*(1 - t)*t*ctrl.x + t**2*to.x;
        const y = (1 - t)**2 * from.y + 2*(1 - t)*t*ctrl.y + t**2*to.y;
        length += Math.hypot(x - prev.x, y - prev.y);
        prev = { x, y };
      }
      return length;
    }

    function createZigzagPath(from, ctrl, to, amp=7, freq, cumulativeOffset=0, isSecondHalf=false) {
      const steps = 250;
      const dt = 1 / steps;
      const path = [];
      let totalLen = 0;
      let prevX = from.x, prevY = from.y;
      const cumsum = [0];

      for (let i = 0; i <= steps; i++) {
        const t = i * dt;
        const x = (1 - t)**2 * from.x + 2*(1 - t)*t*ctrl.x + t**2 * to.x;
        const y = (1 - t)**2 * from.y + 2*(1 - t)*t*ctrl.y + t**2 * to.y;
        if (i > 0) totalLen += Math.hypot(x - prevX, y - prevY);
        cumsum.push(totalLen);
        prevX = x;
        prevY = y;
      }

      prevX = from.x;
      prevY = from.y;
      path.push("M " + Math.round(from.x) + " " + Math.round(from.y));
      for (let i = 1; i <= steps; i++) {
        const t = i * dt;
        const x = (1 - t)**2 * from.x + 2*(1 - t)*t*ctrl.x + t**2 * to.x;
        const y = (1 - t)**2 * from.y + 2*(1 - t)*t*ctrl.y + t**2 * to.y;
        const dxdt = 2*(1 - t)*(ctrl.x - from.x) + 2*t*(to.x - ctrl.x);
        const dydt = 2*(1 - t)*(ctrl.y - from.y) + 2*t*(to.y - ctrl.y);
        const segLen = Math.hypot(dxdt, dydt) || 1;
        const nx = -dydt/segLen, ny = dxdt/segLen;
        const s = cumsum[i];
        const off = amp * Math.sin((cumulativeOffset + s) * freq);

        path.push("L " + Math.round(x + off*nx) + " " + Math.round(y + off*ny));
      }

      if (isSecondHalf) {
        const theta = Math.atan2(to.y - ctrl.y, to.x - ctrl.x);
        path.push("L " + Math.round(to.x + 20*Math.cos(theta)) + " " + Math.round(to.y + 20*Math.sin(theta)));
      }

      return {
        d: path.join(' '),
        totalLength: totalLen
      };
    }

    function getOffsetPoint(x1, y1, x2, y2, offset) {
      const dx = x2 - x1, dy = y2 - y1;
      const dist = Math.hypot(dx, dy);
      if (!dist) return { x: x1, y: y1 };
      return {
        x: x1 + (dx * offset / dist),
        y: y1 + (dy * offset / dist),
      };
    }

    function updatePlayerColor(playerEl, player) {
      const playerColor = player.color || (player.isDefender ? "red" : "black");
      const palette = getGradientPaletteForPlayer(playerColor);
      playerEl.setAttribute("data-player-color", palette.mid);
      playerEl.setAttribute("data-player-base-color", palette.mid);

      updatePlayerGradients(playerEl, palette);

      const neckEl = playerEl.querySelector('[data-role="neck"]');
      if (neckEl) {
        neckEl.setAttribute("fill", palette.mid);
        neckEl.setAttribute("stroke", palette.outline);
      }

      const shortsEl = playerEl.querySelector('[data-role="shorts"]');
      if (shortsEl) {
        shortsEl.setAttribute("fill", palette.shadow);
        shortsEl.setAttribute("stroke", palette.outline);
      }

      const shortsDividerEl = playerEl.querySelector('[data-role="shorts-divider"]');
      if (shortsDividerEl) {
        shortsDividerEl.setAttribute("stroke", adjustColorValue(palette.mid, -0.15));
      }

      const numberBadgeEl = playerEl.querySelector('[data-role="number-badge"]');
      if (numberBadgeEl) {
        numberBadgeEl.setAttribute("fill", "#FFFFFF");
        numberBadgeEl.setAttribute("stroke", "#FFFFFF");
      }

      const footEls = playerEl.querySelectorAll('[data-role^="foot-"]');
      footEls.forEach((foot) => {
        foot.setAttribute("fill", palette.limbSecondary);
        foot.setAttribute("stroke", palette.outline);
      });

      const txt = playerEl.querySelector("text");
      if (txt) {
        txt.setAttribute("fill", palette.mid);
        txt.setAttribute("stroke", "none");
      }

      const outlineEls = playerEl.querySelectorAll('[data-outline-role="primary"]');
      outlineEls.forEach((el) => {
        el.setAttribute("stroke", palette.outline);
      });

      const limbEls = playerEl.querySelectorAll('[data-outline-role="limb"]');
      limbEls.forEach((el) => {
        const variant = el.getAttribute("data-limb-variant");
        if (variant === "secondary") {
          el.setAttribute("stroke", palette.limbShadow);
        } else {
          el.setAttribute("stroke", palette.limb);
        }
      });

      if (player.isSpecialDefender) {
        const specialDefenderCircle = playerEl.querySelector('[data-role="special-ring"]') || playerEl.querySelector('circle[r="24"]');
        if (specialDefenderCircle) {
          const bodyGradientId = playerEl.getAttribute("data-gradient-body");
          if (bodyGradientId) {
            specialDefenderCircle.setAttribute("fill", "url(#" + bodyGradientId + ")");
          } else {
            specialDefenderCircle.setAttribute("fill", palette.mid);
          }
          specialDefenderCircle.setAttribute("stroke", palette.outline);
        }
        const armsGroup = playerEl.querySelector('g.arms-group');
        if (armsGroup) {
          const armPaths = armsGroup.querySelectorAll("path");
          const limbGradientId = playerEl.getAttribute("data-gradient-limb");
          armPaths.forEach((path) => {
            const layer = path.getAttribute("data-arm-layer");
            if (layer === "shadow") {
              path.setAttribute("stroke", palette.shadow);
              path.setAttribute("opacity", "0.7");
            } else if (layer === "highlight") {
              if (limbGradientId) {
                path.setAttribute("stroke", "url(#" + limbGradientId + ")");
              } else {
                path.setAttribute("stroke", palette.limbPrimary);
              }
              path.removeAttribute("opacity");
            } else if (layer === "core") {
              path.setAttribute("stroke", palette.highlight);
              path.setAttribute("opacity", "0.8");
            } else {
              path.setAttribute("stroke", palette.mid);
            }
          });
        }
      }

    }

    function setPlayerPos(playerEl, x, y) {
      const txt = playerEl.querySelector("text");
      if (txt) {
        const offsetAttr = txt.getAttribute("data-offset-y");
        const offsetY = offsetAttr ? parseFloat(offsetAttr) : 0;
        const safeOffsetY = Number.isFinite(offsetY) ? offsetY : 0;
        txt.setAttribute("x", x);
        txt.setAttribute("y", y + safeOffsetY);
      }
      const numberBadge = playerEl.querySelector('[data-role="number-badge"]');
      if (numberBadge) {
        const offsetAttr = numberBadge.getAttribute("data-offset-y");
        const offsetY = offsetAttr ? parseFloat(offsetAttr) : 0;
        const safeOffsetY = Number.isFinite(offsetY) ? offsetY : 0;
        numberBadge.setAttribute("cx", x);
        numberBadge.setAttribute("cy", y + safeOffsetY);
      }
      const figure = playerEl.querySelector('[data-role="figure"]');
      if (figure) {
        figure.setAttribute("transform", "translate(" + x + ", " + y + ")");
      }
      const groundShadow = playerEl.querySelector('[data-role="ground-shadow"]');
      if (groundShadow) {
        const offsetAttr = groundShadow.getAttribute("data-offset-y");
        const offset = offsetAttr ? parseFloat(offsetAttr) : 36;
        const safeOffset = Number.isFinite(offset) ? offset : 36;
        groundShadow.setAttribute("cx", x);
        groundShadow.setAttribute("cy", y + safeOffset);
      }
      const heldBall = playerEl.querySelector('[data-role="held-ball"]');
      if (heldBall) {
        const offsetXAttr = heldBall.getAttribute("data-offset-x");
        const offsetYAttr = heldBall.getAttribute("data-offset-y");
        const offsetX = offsetXAttr ? parseFloat(offsetXAttr) : 28;
        const offsetY = offsetYAttr ? parseFloat(offsetYAttr) : -8;
        let safeOffsetX = Number.isFinite(offsetX) ? offsetX : 28;
        let safeOffsetY = Number.isFinite(offsetY) ? offsetY : -8;
        const isSpecial = playerEl.getAttribute("data-is-special-defender") === "true";
        if (isSpecial) {
          const rotationAttr = playerEl.getAttribute("data-arm-rotation");
          const rotationDegRaw = rotationAttr ? parseFloat(rotationAttr) : 0;
          const rotationDeg = Number.isFinite(rotationDegRaw) ? rotationDegRaw : 0;
          const rotationRad = ((rotationDeg + 90) * Math.PI) / 180;
          const specialRing = playerEl.querySelector('[data-role="special-ring"]');
          const ringRadiusAttr = specialRing?.getAttribute("r");
          const ringRadiusRaw = ringRadiusAttr ? parseFloat(ringRadiusAttr) : 24;
          const ringRadius = Number.isFinite(ringRadiusRaw) ? ringRadiusRaw : 24;
          const distance = ringRadius + 18;
          safeOffsetX = distance * Math.cos(rotationRad);
          safeOffsetY = distance * Math.sin(rotationRad);
        }
        heldBall.setAttribute("transform", "translate(" + (x + safeOffsetX) + ", " + (y + safeOffsetY) + ")");
      }
      // Update special defender circle (if exists)
      const specialDefenderCircle = playerEl.querySelector('circle[r="24"]');
      if (specialDefenderCircle) {
        specialDefenderCircle.setAttribute("cx", x);
        specialDefenderCircle.setAttribute("cy", y);
      }
      // Update arms group transform (if exists - special defender)
      const armsGroup = playerEl.querySelector('g.arms-group');
      if (armsGroup) {
        // Get rotation from data attribute if available, otherwise from transform
        const armRotation = playerEl.getAttribute("data-arm-rotation");
        const rotation = armRotation !== null ? armRotation : (() => {
          const transform = armsGroup.getAttribute("transform");
          const rotationMatch = transform.match(/rotate\(([^)]+)\)/);
          return rotationMatch ? rotationMatch[1] : "0";
        })();
        armsGroup.setAttribute("transform", "translate(" + x + ", " + y + ") rotate(" + rotation + ")");
      }
    }

    function toggleBall(playerEl, hasBall) {
      const heldBall = playerEl.querySelector('[data-role="held-ball"]');
      if (!heldBall) return;
      if (hasBall) {
        heldBall.style.display = "";
      } else {
        heldBall.style.display = "none";
      }
    }

    function removePlayerBall(playerId) {
      const pe = players[playerId];
      if (!pe) return;
      const heldBall = pe.querySelector('[data-role="held-ball"]');
      if (heldBall) heldBall.style.display = "none";
    }

    function ensureEndElement(ln, lineEl) {
      if (!endElements[ln.id]) {
        const fromPlayerEl = players[ln.fromId];
        const playerColor = fromPlayerEl ? fromPlayerEl.getAttribute("data-player-color") : null;
        const color = ln.color || playerColor || "black";

        if (ln.type === "shot") {
          const len = lineEl.getTotalLength();
          if (len < 2) return null;
          const endPoint = lineEl.getPointAtLength(len);
          const nearEndPoint = lineEl.getPointAtLength(Math.max(len - 20, 0));
          const angle = Math.atan2(endPoint.y - nearEndPoint.y, endPoint.x - nearEndPoint.x);
          const EXTEND_DISTANCE = 20;
          const extendedPoint = {
            x: endPoint.x + EXTEND_DISTANCE * Math.cos(angle),
            y: endPoint.y + EXTEND_DISTANCE * Math.sin(angle),
          };

          const shotCircle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
          shotCircle.setAttribute("cx", extendedPoint.x);
          shotCircle.setAttribute("cy", extendedPoint.y);
          shotCircle.setAttribute("r", "18");
          shotCircle.setAttribute("stroke", color);
          shotCircle.setAttribute("stroke-width", "4");
          shotCircle.setAttribute("fill", "none");
          endElements[ln.id] = shotCircle;
        } else if (ln.type === "bar") {
          endElements[ln.id] = createBarEl(lineEl, color);
        } else {
          endElements[ln.id] = createArrowEl(lineEl, color, ln.type);
        }
        if (endElements[ln.id]) {
          // For handoff lines, insert the arrow before the handoff symbol to ensure proper z-index
          if (ln.type === "handoff") {
            const handoffSymbol = document.querySelector('.handoff-symbol');
            if (handoffSymbol && handoffSymbol.parentNode) {
              handoffSymbol.parentNode.insertBefore(endElements[ln.id], handoffSymbol);
            } else {
              svgRoot.appendChild(endElements[ln.id]);
            }
          } else {
            svgRoot.appendChild(endElements[ln.id]);
          }
        }
      }
    }

    function removeEndElement(lineId) {
      if (endElements[lineId]) {
        endElements[lineId].remove?.();
        delete endElements[lineId];
      }
    }

    function removeMovingElement(lineId) {
      if (movingElements[lineId]) {
        movingElements[lineId].remove?.();
        delete movingElements[lineId];
      }
    }

    function showHandoffSymbol(ln, frameLines, baseIndex) {
      // Find the current frame group by finding which one is visible
      const frameGroups = document.querySelectorAll('.frame-lines');
      let currentFrameGroup = null;
      for (let i = 0; i < frameGroups.length; i++) {
        if (frameGroups[i].style.display !== 'none') {
          currentFrameGroup = frameGroups[i];
          break;
        }
      }
      if (!currentFrameGroup) return;
      
      const handoffSymbol = currentFrameGroup.querySelector(\`.handoff-symbol\`);
      if (!handoffSymbol) return;
      
      // Calculate the midpoint of the handoff line
      const fromX = ln.fromX * SCALE_FACTOR;
      const fromY = ln.fromY * SCALE_FACTOR;
      const toX = ln.toX * SCALE_FACTOR;
      const toY = ln.toY * SCALE_FACTOR;
      const midX = (fromX + toX) / 2;
      const midY = (fromY + toY) / 2;
      
      // Fixed orientation for handoff lines (1 horizontal line with 2 vertical lines at 30% and 70%)
      const horizontalLineLength = 40; // Length of horizontal line (scaled)
      const verticalLineLength = 40; // Length of vertical lines (scaled)
      
      // Horizontal line (always horizontal)
      const horizontalStartX = midX - horizontalLineLength / 2;
      const horizontalStartY = midY;
      const horizontalEndX = midX + horizontalLineLength / 2;
      const horizontalEndY = midY;
      
      // First vertical line at 30% of horizontal line
      const firstVerticalX = midX - horizontalLineLength / 2 + (horizontalLineLength * 0.3);
      const line1StartX = firstVerticalX;
      const line1StartY = midY - verticalLineLength / 2;
      const line1EndX = firstVerticalX;
      const line1EndY = midY + verticalLineLength / 2;
      
      // Second vertical line at 70% of horizontal line
      const secondVerticalX = midX - horizontalLineLength / 2 + (horizontalLineLength * 0.7);
      const line2StartX = secondVerticalX;
      const line2StartY = midY - verticalLineLength / 2;
      const line2EndX = secondVerticalX;
      const line2EndY = midY + verticalLineLength / 2;
      
      // Update the handoff symbol lines
      const horizontalLine = handoffSymbol.querySelector('.handoff-horizontal');
      const verticalLine1 = handoffSymbol.querySelector('.handoff-vertical1');
      const verticalLine2 = handoffSymbol.querySelector('.handoff-vertical2');
      
      // Use handoff line color (handoffColor or line color or player color)
      const fromPlayerEl = players[ln.fromId];
      const playerColor = fromPlayerEl ? fromPlayerEl.getAttribute("data-player-color") : null;
      const handoffSymbolColor = ln.handoffColor || ln.color || playerColor || "black";
      
      if (horizontalLine) {
        horizontalLine.setAttribute('x1', horizontalStartX);
        horizontalLine.setAttribute('y1', horizontalStartY);
        horizontalLine.setAttribute('x2', horizontalEndX);
        horizontalLine.setAttribute('y2', horizontalEndY);
        horizontalLine.setAttribute('stroke', handoffSymbolColor);
      }
      
      if (verticalLine1) {
        verticalLine1.setAttribute('x1', line1StartX);
        verticalLine1.setAttribute('y1', line1StartY);
        verticalLine1.setAttribute('x2', line1EndX);
        verticalLine1.setAttribute('y2', line1EndY);
        verticalLine1.setAttribute('stroke', handoffSymbolColor);
      }
      
      if (verticalLine2) {
        verticalLine2.setAttribute('x1', line2StartX);
        verticalLine2.setAttribute('y1', line2StartY);
        verticalLine2.setAttribute('x2', line2EndX);
        verticalLine2.setAttribute('y2', line2EndY);
        verticalLine2.setAttribute('stroke', handoffSymbolColor);
      }
      
      handoffSymbol.style.display = '';
    }

    function hideHandoffSymbol(ln, frameLines, baseIndex) {
      // Find the current frame group by finding which one is visible
      const frameGroups = document.querySelectorAll('.frame-lines');
      let currentFrameGroup = null;
      for (let i = 0; i < frameGroups.length; i++) {
        if (frameGroups[i].style.display !== 'none') {
          currentFrameGroup = frameGroups[i];
          break;
        }
      }
      if (!currentFrameGroup) return;
      
      const handoffSymbol = currentFrameGroup.querySelector(\`.handoff-symbol\`);
      if (handoffSymbol) {
        handoffSymbol.style.display = 'none';
      }
    }

    function createArrowEl(lineEl, color, lineType) {
      const len = lineEl.getTotalLength();
      if (len < 2) return null;
      const endPoint = lineEl.getPointAtLength(len);
      const startPoint = lineEl.getPointAtLength(Math.max(len - 20, 0));
      const angle = Math.atan2(endPoint.y - startPoint.y, endPoint.x - startPoint.x);

      const arrowLength = 36;
      const arrowWidth = Math.PI / 9;
      const baseHeight = -10;
      const offsetLength = -24;

      const adjustedEndPoint = {
        x: endPoint.x - offsetLength * Math.cos(angle),
        y: endPoint.y - offsetLength * Math.sin(angle),
      };

      const arrowPoint1 = {
        x: adjustedEndPoint.x - arrowLength * Math.cos(angle - arrowWidth),
        y: adjustedEndPoint.y - arrowLength * Math.sin(angle - arrowWidth),
      };
      const arrowPoint2 = {
        x: adjustedEndPoint.x - arrowLength * Math.cos(angle + arrowWidth),
        y: adjustedEndPoint.y - arrowLength * Math.sin(angle + arrowWidth),
      };

      const baseMiddle = {
        x: (arrowPoint1.x + arrowPoint2.x) / 2 - baseHeight * Math.cos(angle),
        y: (arrowPoint1.y + arrowPoint2.y) / 2 - baseHeight * Math.sin(angle),
      };

      const arrowPath = "M " + adjustedEndPoint.x + " " + adjustedEndPoint.y + " L " + arrowPoint1.x + " " + arrowPoint1.y + " L " + baseMiddle.x + " " + baseMiddle.y + " L " + arrowPoint2.x + " " + arrowPoint2.y + " Z";
      const pathEl = document.createElementNS("http://www.w3.org/2000/svg", "path");
      pathEl.setAttribute("d", arrowPath);
      
      // Use white transparent color for handoff arrows
      const arrowColor = lineType === "handoff" ? "rgba(255, 255, 255, 0.9)" : color;
      pathEl.setAttribute("stroke", arrowColor);
      pathEl.setAttribute("stroke-width", "4");
      pathEl.setAttribute("fill", arrowColor);
      return pathEl;
    }

    function createBarEl(lineEl, color, barWidth = 60, barLength = 80, offsetLength = 40) {
      const len = lineEl.getTotalLength();
      if (len < 2) return null;
      const endPoint = lineEl.getPointAtLength(len);
      const startPoint = lineEl.getPointAtLength(Math.max(len - 20, 0));
      const angle = Math.atan2(endPoint.y - startPoint.y, endPoint.x - startPoint.x);

      const adjustedEndPoint = {
        x: endPoint.x + offsetLength * Math.cos(angle),
        y: endPoint.y + offsetLength * Math.sin(angle),
      };

      const barCenter = {
        x: adjustedEndPoint.x - (barLength / 2) * Math.cos(angle),
        y: adjustedEndPoint.y - (barLength / 2) * Math.sin(angle),
      };

      const barEnd1 = {
        x: barCenter.x + (barWidth / 2) * Math.cos(angle + Math.PI / 2),
        y: barCenter.y + (barWidth / 2) * Math.sin(angle + Math.PI / 2),
      };
      const barEnd2 = {
        x: barCenter.x - (barWidth / 2) * Math.cos(angle + Math.PI / 2),
        y: barCenter.y - (barWidth / 2) * Math.sin(angle + Math.PI / 2),
      };

      const barPath = "M " + barEnd1.x + " " + barEnd1.y + " L " + barEnd2.x + " " + barEnd2.y;
      const pathEl = document.createElementNS("http://www.w3.org/2000/svg", "path");
      pathEl.setAttribute("d", barPath);
      pathEl.setAttribute("stroke", color);
      pathEl.setAttribute("stroke-width", "4");
      pathEl.setAttribute("fill", "none");
      pathEl.setAttribute("stroke-linecap", "round");
      pathEl.setAttribute("stroke-linejoin", "round");
      return pathEl;
    }

    function resetAll() {
      Object.values(players).forEach(pe => {
        pe.style.display = "none";
      });
      document.querySelectorAll(".frame-lines path").forEach(le => {
        le.style.display = "none";
        le.removeAttribute("stroke-dasharray");
        le.removeAttribute("stroke-dashoffset");
      });
      // Hide all handoff symbols
      document.querySelectorAll(".handoff-symbol").forEach(symbol => {
        symbol.style.display = "none";
      });
      Object.values(cones).forEach(ce => {
        ce.style.display = "none";
      });
      Object.values(movingElements).forEach(e => e?.remove?.());
      for (let k in movingElements) delete movingElements[k];
      Object.values(endElements).forEach(el => el?.remove?.());
      for (let k in endElements) delete endElements[k];
    }
  `;

  return script;
};

// FINAL HTML GENERATION
export default function PlayAnimationHTMLDoc3DPlayers(frames: Frame[], courtType: CourtType, play: PlayMeta, native: boolean, autoDownload: boolean = false, courtColors?: {
  lineColor?: string;
  paintAreaColor?: string | null;
  paintAreaUseWoodPattern?: boolean;
  midRangeColor?: string | null;
  midRangeUseWoodPattern?: boolean;
  outOfBoundsColor?: string | null;
  outOfBoundsUseWoodPattern?: boolean;
}) {
  let downloadButtonStyle = 'normal';
  if (native)
    downloadButtonStyle = 'none';
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>SVG Animation - High Quality</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: sans-serif;
    }
    #controls {
      text-align: center;
      margin: 20px 0;
    }
    #play-pause, #download-video {
      padding: 6px 12px;
      font-size: 14px;
      cursor: pointer;
      background-color: #fff;
      color: #333;
      border: 1px solid #ccc;
      border-radius: 4px;
      outline: none;
      margin: 0 5px;
      transition: all 0.2s;
    }

    #play-pause:hover:not(:disabled), #download-video:hover:not(:disabled) {
      background-color: #f0f0f0;
      border-color: #999;
    }
    #play-pause:disabled, #download-video:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    #progress-bar {
      width: 90%;
      margin-top: 12px;
      height: 8px;
      border-radius: 4px;
    }
    svg {
      display: block;
      margin: 0 auto;
      background-color: #fff;
      border: 2px solid #ccc;
      border-radius: 6px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
  </style>
</head>
<body>
  ${generateSVGContent(frames, courtType, courtColors)}

  <div id="controls">
    <button id="play-pause">▶</button>
    <button id="download-video" style="display: ${downloadButtonStyle};">${i18next.t('PlayAnimationHTMLDoc00')}</button>
    <input type="range" id="progress-bar" min="0" max="1000" value="0" />
  </div>

  <script>
    ${generateAnimationScript(frames, play, autoDownload)}
  </script>

</body>
</html>
  `;
}